import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-1b2e5f21"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 1,
  class: "route-planner-view"
}
const _hoisted_2 = { class: "mobile-header" }
const _hoisted_3 = { class: "main-container" }
const _hoisted_4 = { class: "content-area" }
const _hoisted_5 = { class: "map-container" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "arrow" }, null, -1))
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = { class: "booking-content" }

import _ from 'lodash'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import RouteMap from '@/components/public/route-planner/RouteMap.vue'
import BookingWidgetView from '@/views/public/BookingWidgetView.vue'
import {
  useAddStopMutation,
  useCreateRouteMutation,
  useDeleteStopMutation,
  useGetRouteMutation,
  useRoutePlanningServicesQuery,
  useUpdateDrivingSegmentsMutation,
  useUpdateRouteOrderMutation,
  useUpdateRouteStopsMutation,
  useUpdateRouteMutation,
  useDeleteAllStopsMutation,
} from '@/queries/public/services'
import { computed, onMounted, ref, watch } from 'vue'
import { PublicService } from '@/models/public/bookingWidget.model'
import RoutePanel from '@/components/public/route-planner/RoutePanel.vue'
import {
  JourneyDuration,
  Route,
  RouteStop,
} from '@/models/public/routePlanner.model'
import {
  calculateNewSegments,
  calculateServiceETA,
  parseRoute,
  extractAddressInfo,
  extractServiceInfos,
} from '@/utils/route-planner'
import { Order } from '@/models/public/order.model'
import { useRoute } from 'vue-router'
import { format, formatISO, addDays, addWeeks, addMonths } from 'date-fns'
import { FORMATS, SIZES } from '@/enums/components'
import BaseButton from '@/components/BaseButton.vue'
import TouringeryLogo from '@/components/TouringeryLogo.vue'
import { AddressDetailsPayload } from '@/models/location.model'
import { useCreateMapOrderMappingMutation } from '@/queries/dmoMapQueries'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/store/auth'
import { Option } from '@/models/shared.model'


export default /*#__PURE__*/_defineComponent({
  __name: 'RoutePlannerView',
  setup(__props) {

const { query } = useRoute()
const authStore = useAuthStore()
const { selectedCompanyId } = storeToRefs(authStore)

const showMobilePanel = ref(false)
const showBookingWidget = ref(false)
const journeyDuration = ref<JourneyDuration | null>(null)
const startTime = ref<Date | null>(new Date())
const selectedMapId = ref<number | null>(null)

const filters = ref({
  search: '',
  searchCategories: '',
  dmoMap: '',
})

const queryStartDate = computed(() => {
  return startTime.value
    ? format(startTime.value, 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd')
})

const queryEndDate = computed(() => {
  const baseDate = startTime.value || new Date()
  switch (journeyDuration.value) {
    case JourneyDuration.DAY:
      return format(addDays(baseDate, 1), 'yyyy-MM-dd')
    case JourneyDuration.WEEK:
      return format(addWeeks(baseDate, 1), 'yyyy-MM-dd')
    case JourneyDuration.MONTH:
      return format(addMonths(baseDate, 1), 'yyyy-MM-dd')
    default:
      return null
  }
})

const {
  data: servicesData,
  isLoading,
  refetch: refetchServices,
} = useRoutePlanningServicesQuery(queryStartDate, queryEndDate, filters)

const { mutateAsync: createRoute } = useCreateRouteMutation()
const { mutateAsync: getRoute } = useGetRouteMutation()
const { mutateAsync: createStop } = useAddStopMutation()
const { mutateAsync: deleteStop } = useDeleteStopMutation()
const { mutateAsync: deleteAllStops } = useDeleteAllStopsMutation()
const { mutateAsync: updateSegments } = useUpdateDrivingSegmentsMutation()
const { mutateAsync: updateRouteStops } = useUpdateRouteStopsMutation()
const { mutateAsync: updateRouteOrder } = useUpdateRouteOrderMutation()
const { mutateAsync: updateRoute } = useUpdateRouteMutation()
const { mutateAsync: createMapOrderMapping } =
  useCreateMapOrderMappingMutation(selectedCompanyId)

const services = ref<PublicService[]>([])
const route = ref<Route | null>(null)
const selectedService = ref<PublicService | null>(null)
const serviceInfos = ref<Record<number, any>>({})

const defaultAddress = {
  city: '',
  country: '',
  county: '',
  google_address: '',
  latitude: '',
  longitude: '',
  number: '',
  street: '',
  zip_code: '',
}

const startPoint = ref<AddressDetailsPayload>({ ...defaultAddress })
const endPoint = ref<AddressDetailsPayload>({ ...defaultAddress })

const debouncedSync = _.debounce(handleSyncStops, 1000)

const selectedServiceEarliestStart = computed(() =>
  calculateServiceETA(selectedService.value, route.value),
)

const hasStartAndEndPoints = computed(() => {
  return !!(
    startPoint.value?.google_address?.trim() &&
    endPoint.value?.google_address?.trim()
  )
})

const selectedServiceStart = computed(() => {
  if (!selectedService.value || !route.value?.stops) return null

  const stop = route.value.stops.find(
    (stop) => stop.service.id === selectedService.value?.id,
  )
  return stop?.start_time || null
})

const selectedServiceEnd = computed(() => {
  if (!selectedService.value || !route.value?.stops) return null

  const stop = route.value.stops.find(
    (stop) => stop.service.id === selectedService.value?.id,
  )
  return stop?.end_time || null
})

const toggleMobilePanel = () => {
  showMobilePanel.value = !showMobilePanel.value
}

const toggleBookingWidget = () => {
  showBookingWidget.value = !showBookingWidget.value
}

const handleSearchChange = (search: string) => {
  filters.value.search = search
  refetchServices.value()
}

const handleSearchCategoriesChange = (searchCategories: string) => {
  filters.value.searchCategories = searchCategories
  refetchServices.value()
}

const handleMapFilterChange = (mapId: string) => {
  filters.value.dmoMap = mapId
  refetchServices.value()
}

onMounted(async () => {
  if (!query.routeHash) return

  try {
    const existingRoute = await getRoute(query.routeHash as string)
    if (existingRoute) {
      const parsedRoute = parseRoute(existingRoute, true)
      route.value = parsedRoute

      if (parsedRoute.start_time) {
        startTime.value = parsedRoute.start_time
      }

      const { startPoint: extractedStartPoint, endPoint: extractedEndPoint } =
        extractAddressInfo(parsedRoute)
      if (extractedStartPoint) {
        startPoint.value = extractedStartPoint
      }
      if (extractedEndPoint) {
        endPoint.value = extractedEndPoint
      }

      const extractedServiceInfos = extractServiceInfos(parsedRoute)
      serviceInfos.value = { ...serviceInfos.value, ...extractedServiceInfos }

      recalculateStopTimes()
    }
  } catch (error) {
    console.error(error)
  }
})

watch([queryStartDate, queryEndDate], async () => {
  try {
    await refetchServices.value()
    services.value = servicesData.value || []
  } catch (error) {
    console.error('Failed to refetch services:', error)
  }
})

watch(
  () => servicesData.value,
  () => {
    services.value = servicesData.value || []
  },
  { immediate: true },
)

watch(
  () => route.value?.stops,
  (newStops, prevStops) => {
    if (!_.isEqual(newStops, prevStops)) {
      debouncedSync()
    }
  },
  { deep: true },
)

watch(
  () => serviceInfos.value,
  () => {
    setStopPropertiesFromServiceInfos()
  },
  { deep: true },
)

const handleMapSelect = (map: Option | null) => {
  selectedMapId.value = map?.key as number
}

const handleStartTimeChange = async (newStartTime: Date | null) => {
  startTime.value = newStartTime

  if (route.value && route.value.stops?.length > 0) {
    try {
      const updatedRoute = await deleteAllStops(route.value.id)
      route.value = parseRoute(updatedRoute)
      selectedService.value = null
      serviceInfos.value = {}
    } catch (e) {
      console.error('Failed to clear route stops:', e)
      route.value = null
    }
  }
}

const handleStartPointChange = async (value: AddressDetailsPayload) => {
  startPoint.value = value
  await handleUpdateRoute()
}

const handleStartPointUpdate = (value: string) => {
  startPoint.value = { ...startPoint.value, google_address: value }
}

const handleEndPointChange = async (value: AddressDetailsPayload) => {
  endPoint.value = value
  await handleUpdateRoute()
}

const handleEndPointUpdate = (value: string) => {
  endPoint.value = { ...endPoint.value, google_address: value }
}

const handleMoveStopUp = async ({
  stop,
  index,
}: {
  stop: RouteStop
  index: number
}) => {
  if (index === 0 || !route.value?.stops) return

  const updatedStops = [...route.value.stops]
  const [movedStop] = updatedStops.splice(index, 1)
  updatedStops.splice(index - 1, 0, movedStop)

  const stopsWithNewOrder = updatedStops.map((stop, index) => ({
    ...stop,
    order_index: index,
  }))

  route.value = {
    ...route.value,
    stops: stopsWithNewOrder,
  }
}

const handleMoveStopDown = async ({
  stop,
  index,
}: {
  stop: RouteStop
  index: number
}) => {
  if (!route.value?.stops || index === route.value.stops.length - 1) return

  const updatedStops = [...route.value.stops]
  const [movedStop] = updatedStops.splice(index, 1)
  updatedStops.splice(index + 1, 0, movedStop)

  const stopsWithNewOrder = updatedStops.map((stop, index) => ({
    ...stop,
    order_index: index,
  }))

  route.value = {
    ...route.value,
    stops: stopsWithNewOrder,
  }
}

const handleJourneyDurationChange = async ({
  key,
}: {
  key: JourneyDuration
}) => {
  journeyDuration.value = key
  if (route.value && route.value.stops?.length > 0) {
    try {
      const updatedRoute = await deleteAllStops(route.value.id)
      route.value = parseRoute(updatedRoute)
      selectedService.value = null
      serviceInfos.value = {}
    } catch (e) {
      console.error('Failed to clear route stops:', e)
      route.value = null
    }
  }
}

const handleUpdateRoute = async () => {
  let currentRoute = route.value
  if (!currentRoute) {
    currentRoute = await handleCreateRoute()
  }

  if (!currentRoute) return

  try {
    const updatedRoute = await updateRoute({
      route: currentRoute.id,
      start_time: startTime.value ? formatISO(startTime.value) : null,
      start_point: startPoint.value,
      end_point: endPoint.value,
    })
    route.value = parseRoute(updatedRoute)
  } catch (e) {
    console.error(e)
  }
}

const handleAdd = async (service: PublicService) => {
  handleSelectService(service)
  if (!route.value) {
    route.value = await handleCreateRoute()
  }
  handleAddStop(service)
  showMobilePanel.value = false
}

const handleDelete = async (stop: RouteStop) => {
  try {
    const shouldClearSelected = stop.service.id === selectedService.value?.id
    const updatedRoute = await deleteStop(stop)
    if (updatedRoute) {
      route.value = parseRoute(updatedRoute)
      if (shouldClearSelected) {
        selectedService.value = null
      }
    }
  } catch (e) {
    console.error(e)
  }
}

const handleCreateRoute = async () => {
  try {
    return await createRoute()
  } catch (e) {
    console.error(e)
  }
  return null
}

async function handleSyncStops() {
  try {
    if (!route.value?.stops) return

    const updatedRoute = await updateRouteStops({
      route: route.value.id,
      stops: route.value.stops.map((stop) => ({
        id: stop.id,
        order_index: stop.order_index,
        start_time: stop.start_time ? stop.start_time : null,
        end_time: stop.end_time ? stop.end_time : null,
      })),
    })
    if (updatedRoute) {
      route.value = parseRoute(updatedRoute)
      setStopPropertiesFromServiceInfos()
    }
  } catch (e) {
    console.error(e)
  }
}

const recalculateStopTimes = () => {
  if (!route.value?.stops || !startTime.value) return
  recalculateFromStopIndex(0)
}

const recalculateFromStopIndex = (fromIndex: number) => {
  if (!route.value?.stops) return

  for (let i = fromIndex; i < route.value.stops.length; i++) {
    const stop = route.value.stops[i]
    const serviceETA = calculateServiceETA(stop.service, route.value)
    const isLastStop = i === route.value.stops.length - 1

    if (stop.service.type === 'HOUSING') {
      const housingInfo = stop.service.housings_information
      if (housingInfo && serviceETA) {
        const checkInDate = new Date(serviceETA)

        const existingEndTime = serviceInfos.value[stop.service.id]?.end_time
        let checkOutDate

        if (
          isLastStop &&
          existingEndTime &&
          new Date(existingEndTime) > checkInDate
        ) {
          checkOutDate = new Date(existingEndTime)
        } else {
          checkOutDate = new Date(checkInDate)
          checkOutDate.setDate(checkOutDate.getDate() + 1)
          const [checkOutHours, checkOutMinutes] = housingInfo.check_out_until
            .split(':')
            .map(Number)
          checkOutDate.setHours(checkOutHours, checkOutMinutes, 0, 0)
        }

        serviceInfos.value = {
          ...serviceInfos.value,
          [stop.service.id]: {
            start_time: checkInDate,
            end_time: checkOutDate,
          },
        }
      }
    } else if (serviceETA) {
      serviceInfos.value = {
        ...serviceInfos.value,
        [stop.service.id]: {
          start_time: serviceETA,
          end_time: serviceETA,
        },
      }
    }
  }
}

const handleAddStop = async (service: PublicService) => {
  try {
    if (!route.value) return

    const updatedRoute = await createStop({
      route: route.value.id,
      service: service.id,
      start_time: null,
      end_time: null,
    })

    if (updatedRoute) {
      route.value = parseRoute(updatedRoute)

      if (service.type === 'HOUSING') {
        const housingInfo = service.housings_information
        const serviceETA = calculateServiceETA(service, route.value)

        if (housingInfo && serviceETA) {
          const checkInDate = new Date(serviceETA)

          const checkOutDate = new Date(checkInDate)
          checkOutDate.setDate(checkOutDate.getDate() + 1)

          const [checkOutHours, checkOutMinutes] = housingInfo.check_out_until
            .split(':')
            .map(Number)
          checkOutDate.setHours(checkOutHours, checkOutMinutes, 0, 0)

          serviceInfos.value = {
            ...serviceInfos.value,
            [service.id]: {
              start_time: checkInDate,
              end_time: checkOutDate,
            },
          }
        }
      }

      setStopPropertiesFromServiceInfos()
    }
  } catch (e) {
    console.error(e)
  }
}

const handleUpdateSegments = async (googleMapsRoute) => {
  try {
    if (!route.value) return

    const segments = calculateNewSegments(googleMapsRoute, route.value)
    await updateSegments({
      route: route.value.id,
      segments,
    })
  } catch (e) {
    console.error(e)
  }
}

const handleOrderChange = async (order: Order) => {
  if (!route.value) return
  try {
    const updatedRoute = await updateRouteOrder({
      id: route.value.id,
      order: order.id!,
    })
    route.value = parseRoute(updatedRoute)

    if (selectedMapId.value) {
      await createMapOrderMapping({
        map_id: selectedMapId.value,
        order_id: order.id!,
      })
    }
  } catch (e) {
    console.error(e)
  }
}

const handleSelectService = (
  newSelectedService: PublicService,
  shouldRefresh = false,
) => {
  if (shouldRefresh || selectedService.value?.id !== newSelectedService.id) {
    selectedService.value = newSelectedService
    if (window.innerWidth <= 768) {
      showBookingWidget.value = true
    }
  }
}

const handleDateChange = (
  property: 'start_time' | 'end_time',
  newDate: Date | null,
) => {
  if (!selectedService.value) return

  const serviceId = selectedService.value.id

  serviceInfos.value = {
    ...serviceInfos.value,
    [serviceId]: {
      ...serviceInfos.value[serviceId],
      [property]: newDate,
    },
  }

  setStopPropertiesFromServiceInfos()

  if (property === 'end_time' && route.value?.stops) {
    const changedStopIndex = route.value.stops.findIndex(
      (stop) => stop.service.id === serviceId,
    )
    if (
      changedStopIndex !== -1 &&
      changedStopIndex < route.value.stops.length - 1
    ) {
      recalculateFromStopIndex(changedStopIndex + 1)
    }
  }
}

const setStopPropertiesFromServiceInfos = () => {
  if (!route.value) return

  const updatedStops = route.value.stops.map((stop) => {
    const serviceInfo = serviceInfos.value[stop.service.id]
    if (serviceInfo) {
      return {
        ...stop,
        start_time: serviceInfo.start_time,
        end_time: serviceInfo.end_time,
      }
    }
    return stop
  })

  route.value = {
    ...route.value,
    stops: updatedStops,
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (_unref(isLoading))
      ? (_openBlock(), _createBlock(LoadingSpinner, { key: 0 }))
      : (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(BaseButton, {
              icon: "menu",
              size: _unref(SIZES).SMALL,
              format: _unref(FORMATS).ICON_CIRCULAR,
              onClick: toggleMobilePanel,
              class: _normalizeClass({ active: showMobilePanel.value })
            }, null, 8, ["size", "format", "class"]),
            _createVNode(TouringeryLogo, {
              width: 102,
              height: 20,
              icon: 'logo-provider'
            })
          ]),
          (showMobilePanel.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "mobile-overlay",
                onClick: toggleMobilePanel
              }))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("aside", {
              class: _normalizeClass(["sidebar", { 'mobile-open': showMobilePanel.value }])
            }, [
              _createElementVNode("div", { class: "sidebar-header" }, [
                _createElementVNode("button", {
                  class: "close-btn",
                  onClick: toggleMobilePanel
                }, "×")
              ]),
              _createVNode(RoutePanel, {
                services: services.value,
                route: route.value,
                "start-time": startTime.value,
                "start-point": startPoint.value,
                "end-point": endPoint.value,
                onOnChange: _cache[0] || (_cache[0] = (value) => (services.value = value)),
                onOnSelect: _cache[1] || (_cache[1] = (value) => handleSelectService(value, false)),
                onOnRefresh: _cache[2] || (_cache[2] = (value) => handleSelectService(value, true)),
                onOnDelete: _cache[3] || (_cache[3] = (value) => handleDelete(value)),
                onOnStartTimeChange: _cache[4] || (_cache[4] = (value) => handleStartTimeChange(value)),
                onOnStartPointChange: _cache[5] || (_cache[5] = (value) => handleStartPointChange(value)),
                onOnStartPointUpdate: _cache[6] || (_cache[6] = (value) => handleStartPointUpdate(value)),
                onOnEndPointChange: _cache[7] || (_cache[7] = (value) => handleEndPointChange(value)),
                onOnEndPointUpdate: _cache[8] || (_cache[8] = (value) => handleEndPointUpdate(value)),
                onOnMoveStopUp: _cache[9] || (_cache[9] = (value) => handleMoveStopUp(value)),
                onOnMoveStopDown: _cache[10] || (_cache[10] = (value) => handleMoveStopDown(value)),
                onOnJourneyDurationChange: _cache[11] || (_cache[11] = 
              (value) => handleJourneyDurationChange(value)
            ),
                onOnMapSelect: _cache[12] || (_cache[12] = (value) => handleMapSelect(value)),
                onOnSearchChange: _cache[13] || (_cache[13] = (value) => handleSearchChange(value)),
                onOnSearchCategoriesChange: _cache[14] || (_cache[14] = 
              (value) => handleSearchCategoriesChange(value)
            ),
                onOnMapFilterChange: _cache[15] || (_cache[15] = (value) => handleMapFilterChange(value))
              }, null, 8, ["services", "route", "start-time", "start-point", "end-point"])
            ], 2),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(RouteMap, {
                  route: route.value,
                  services: services.value,
                  "selected-service": selectedService.value,
                  "disable-route-planning": !_unref(hasStartAndEndPoints),
                  onOnAdd: _cache[16] || (_cache[16] = (value) => handleAdd(value)),
                  onOnUpdateSegments: _cache[17] || (_cache[17] = (value) => handleUpdateSegments(value))
                }, null, 8, ["route", "services", "selected-service", "disable-route-planning"])
              ]),
              _createElementVNode("div", {
                class: _normalizeClass(["booking-container", { 'mobile-expanded': showBookingWidget.value }])
              }, [
                _createElementVNode("div", {
                  class: "booking-header",
                  onClick: toggleBookingWidget
                }, [
                  _createElementVNode("button", {
                    class: _normalizeClass(["toggle-btn", { active: showBookingWidget.value }])
                  }, _hoisted_7, 2)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(BookingWidgetView, {
                    service: selectedService.value,
                    "earliest-start": _unref(selectedServiceEarliestStart),
                    start: _unref(selectedServiceStart),
                    end: _unref(selectedServiceEnd),
                    onOnStartChange: _cache[18] || (_cache[18] = 
                  (value) => handleDateChange('start_time', value)
                ),
                    onOnEndChange: _cache[19] || (_cache[19] = (value) => handleDateChange('end_time', value)),
                    onOnOrderChange: _cache[20] || (_cache[20] = (value) => handleOrderChange(value))
                  }, null, 8, ["service", "earliest-start", "start", "end"])
                ])
              ], 2)
            ])
          ])
        ]))
  ]))
}
}

})